import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useGlobalFilters } from '../contexts/FilterContext';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Chip,
  Grid,
  InputAdornment,
  IconButton,
  Tooltip,
  Checkbox,
  ListItemText,
  OutlinedInput,
  ListSubheader,
  Link,
} from '@mui/material';
import {
  Search,
  GetApp,
  Refresh as RefreshIcon,
  CheckCircle,
  Cancel,
  Warning,
  FilterList,
  Circle,
  ExpandMore,
  ExpandLess,
  HelpOutline,
  CloudOutlined,
  Apps,
} from '@mui/icons-material';
import { RESILIENCY_MSA } from '../config/api';
import { authFetch } from '../services/authFetch';

// ─── Types ────────────────────────────────────────────────────────────────────

interface MSARow {
  id: number;
  app_id: string;
  application: string;
  app_group: string;
  app_type: string;
  platform: string;
  platform_portfolio: string;
  bcp_available: string;
  backup_available: string;
  trp_available: string;
  trp_last_updated_date: string;
  mvc_certification_date: string;
  rpo_business_expectation: string;
  rto_business_expectation: string;
  current_rpo_setup: string;
  current_rto_setup: string;
  failover_setup: string;
  failover_mechanism: string;
  failover_type: string;
  failover_tested_date_last_12_months: string;
  restore_from_backup_tested_last_12_months: string;
  hosting_scope: string;
  key_data_sets_available: string;
  descoped: string;
}

type CalcResult = { pct: number; status: 'Yes' | 'No' | 'NA' };

interface PortfolioMetrics {
  portfolio: string;
  count: number;
  // each metric: { yes, nonNA, pct }
  rpo:     { yes: number; nonNA: number; pct: number };
  rto:     { yes: number; nonNA: number; pct: number };
  trp:     { yes: number; nonNA: number; pct: number };
  backup:  { yes: number; nonNA: number; pct: number };
  restore: { yes: number; nonNA: number; pct: number; planned: number; unplanned: number };
  failoverExists: { yes: number; nonNA: number; pct: number };
  failoverTested: { yes: number; nonNA: number; pct: number; planned: number; unplanned: number };
  // total column = sum of yes / sum of nonNA across 7 metrics
  totalYes:   number;
  totalNonNA: number;
  totalPct:   number;
  // sub-rows keyed by platform
  platforms: Record<string, PortfolioMetrics>;
  // raw app rows at this level (used for application sub-rows)
  appRows: MSARow[];
}

// ─── Calculation helpers (identical logic to Resilience.tsx) ─────────────────

/** Safely coerce any API value to a trimmed string. Returns '' for null/undefined/non-printable. */
const toStr = (value: any): string => {
  if (value === null || value === undefined) return '';
  return String(value).trim();
};

/** Backup Available: TRUE/YES/Y/SAAS=Yes, FALSE/NO/N/0=No, NULL/blank=NA (excluded), NA/N/A/OOS=NA (excluded) */
const calcSimpleYesNo = (value: any): CalcResult => {
  const v = toStr(value).toUpperCase();
  if (!v) return { pct: 0, status: 'NA' };                                      // NULL from DB → excluded
  if (v === 'NA' || v === 'N/A' || v === 'OOS') return { pct: 0, status: 'NA' };
  if (v === 'YES' || v === 'Y' || v === 'TRUE' || v === 'SAAS') return { pct: 100, status: 'Yes' };
  if (v === 'NO' || v === 'N' || v === 'FALSE' || v === '0') return { pct: 0, status: 'No' };
  return { pct: 0, status: 'No' };
};

/** TRP Available: matches calculateTrpPercentage — only YES/TRUE=Yes, NO/N/FALSE/0=No, NA/N/A/OOS=excluded
 *  NOTE: SAAS is intentionally NOT Yes for TRP (unlike Backup); matches Resilience.tsx exactly.
 *  TRUE/FALSE added to handle PostgreSQL boolean columns returned by the DB. */
const calcTrpAvailable = (value: any): CalcResult => {
  const v = toStr(value).toUpperCase();
  if (!v) return { pct: 0, status: 'No' };
  if (v === 'NA' || v === 'N/A' || v === 'OOS') return { pct: 0, status: 'NA' };
  if (v === 'YES' || v === 'TRUE') return { pct: 100, status: 'Yes' };
  if (v === 'NO' || v === 'N' || v === 'FALSE' || v === '0') return { pct: 0, status: 'No' };
  return { pct: 0, status: 'No' };
};

/** Defined RPO/RTO: matches calculateRpoRtoPercentage from Resilience.tsx exactly */
const calcRpoRto = (value: any): CalcResult => {
  const v = toStr(value);
  if (!v) return { pct: 0, status: 'No' };
  const u = v.toUpperCase();
  // NA/N/A/OOS are excluded (not counted in denominator)
  if (u === 'NA' || u === 'N/A' || u === 'OOS') return { pct: 0, status: 'NA' };
  // Explicit No
  if (u === 'NO' || u === '0') return { pct: 0, status: 'No' };
  // Time format: HH:MM, HH:MM:SS, :MM
  if (/^(\d{1,2}:)?\d{1,2}(:\d{2})?$/.test(v)) return { pct: 100, status: 'Yes' };
  // Minute format: "20-25 mins", "5 min"
  if (/\d+\s*-?\s*\d*\s*(min|mins)/i.test(v)) return { pct: 100, status: 'Yes' };
  // Recognised yes-meaning keywords
  if (u === 'SAAS' || u === 'YES' || u === 'IAAS' || u === 'NOT DEFINED' || u === '?') return { pct: 100, status: 'Yes' };
  // Positive number
  const num = parseFloat(v);
  if (!isNaN(num) && num > 0) return { pct: 100, status: 'Yes' };
  if (!isNaN(num) && num === 0) return { pct: 0, status: 'No' };
  // Default: unrecognised value = No (matches Resilience default)
  return { pct: 0, status: 'No' };
};

/** Failover Exists:
 *  NULL/blank = NA (excluded from calculation)
 *  NA/N/A/OOS = NA (excluded)
 *  "Not Setup" / NO / N / FALSE = No
 *  "Multi Region" / "Multi Site" / "Multi Zone" / any other non-No value = Yes */
const calcFailoverSetup = (value: any): CalcResult => {
  const v = toStr(value).toUpperCase();
  if (!v) return { pct: 0, status: 'NA' };                 // NULL from DB → excluded
  if (v === 'NA' || v === 'N/A' || v === 'OOS') return { pct: 0, status: 'NA' };
  if (v === 'NOT SETUP' || v === 'NO' || v === 'N' || v === 'FALSE') return { pct: 0, status: 'No' };
  return { pct: 100, status: 'Yes' };
};

/** Parse date strings in multiple formats including DB datetime strings.
 *  Handles: MM/DD/YYYY, DD-MM-YYYY, YYYY-MM-DD, and native formats
 *  ("Mon, 08 Jul 2024 00:00:00 GMT", ISO strings, etc.) returned by the DB. */
const parseDateStr = (value: any): Date | null => {
  const v = toStr(value);
  if (!v) return null;
  const u = v.toUpperCase();
  if (u === 'OOS' || u === 'NA' || u === 'N/A' || u === 'NO') return null;

  // MM/DD/YYYY
  const slashMatch = v.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slashMatch) {
    const d = new Date(parseInt(slashMatch[3]), parseInt(slashMatch[1]) - 1, parseInt(slashMatch[2]));
    return isNaN(d.getTime()) ? null : d;
  }
  // DD-MM-YYYY
  const dashDMY = v.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (dashDMY) {
    const d = new Date(parseInt(dashDMY[3]), parseInt(dashDMY[2]) - 1, parseInt(dashDMY[1]));
    return isNaN(d.getTime()) ? null : d;
  }
  // YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS (DB returns "2026-02-25T00:00:00" format)
  const isoMatch = v.match(/^(\d{4})-(\d{2})-(\d{2})(T.*)?$/);
  if (isoMatch) {
    const d = new Date(parseInt(isoMatch[1]), parseInt(isoMatch[2]) - 1, parseInt(isoMatch[3]));
    return isNaN(d.getTime()) ? null : d;
  }
  // Fallback: native Date parse — handles RFC7231 strings like
  // "Wed, 18 Feb 2026 00:00:00 GMT" returned by the DB for datetime columns.
  const native = new Date(v);
  return isNaN(native.getTime()) ? null : native;
};

/** Date-tested-in-last-12-months:
 *  NULL/blank = NA (excluded from calculation)
 *  NA/N/A/OOS = NA (excluded)
 *  NO or unparseable = No
 *  future date = No (not yet tested)
 *  within last 12 months = Yes
 *  older than 12 months = No */
const calcDateLast12M = (value: any): CalcResult => {
  const v = toStr(value);
  if (!v) return { pct: 0, status: 'NA' };                 // NULL from DB → excluded
  const u = v.toUpperCase();
  if (u === 'OOS' || u === 'NA' || u === 'N/A') return { pct: 0, status: 'NA' };
  if (u === 'NO') return { pct: 0, status: 'No' };
  const d = parseDateStr(value);
  if (!d) return { pct: 0, status: 'No' };
  const now = new Date(); now.setHours(0, 0, 0, 0);
  // Future date or today = planned but not yet done
  if (d >= now) return { pct: 0, status: 'No' };
  const twelveMonthsAgo = new Date();
  twelveMonthsAgo.setMonth(twelveMonthsAgo.getMonth() - 12);
  twelveMonthsAgo.setHours(0, 0, 0, 0);
  return d >= twelveMonthsAgo
    ? { pct: 100, status: 'Yes' }
    : { pct: 0, status: 'No' };
};

/** Compute planned/unplanned counts from an array of date-field values */
const countPlannedUnplanned = (values: any[]) => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  let planned = 0; let unplanned = 0;
  values.forEach(v => {
    const r = calcDateLast12M(v);
    if (r.pct === 0 && r.status !== 'NA') {       // not already tested, not excluded
      const d = parseDateStr(v);
      if (d && d >= today) planned++;
      else unplanned++;
    }
  });
  return { planned, unplanned };
};

/** Aggregate CalcResult[] into { yes, nonNA, pct } */
const aggregate = (results: CalcResult[]) => {
  const nonNA = results.filter(r => r.status !== 'NA');
  const yes   = results.filter(r => r.status === 'Yes').length;
  const pct   = nonNA.length > 0 ? Math.round(yes / nonNA.length * 100) : 0;
  return { yes, nonNA: nonNA.length, pct };
};

/** Build PortfolioMetrics for an array of MSARow (one portfolio or platform level) */
const buildMetrics = (apps: MSARow[], portfolio: string): PortfolioMetrics => {
  const rpo    = aggregate(apps.map(a => calcRpoRto(a.rpo_business_expectation)));
  const rto    = aggregate(apps.map(a => calcRpoRto(a.rto_business_expectation)));
  const trp    = aggregate(apps.map(a => calcTrpAvailable(a.trp_available)));
  const backup = aggregate(apps.map(a => calcSimpleYesNo(a.backup_available)));

  const restoreResults = apps.map(a => calcDateLast12M(a.restore_from_backup_tested_last_12_months));
  const restoreAgg     = aggregate(restoreResults);
  const { planned: rPlanned, unplanned: rUnplanned } = countPlannedUnplanned(
    apps.map(a => a.restore_from_backup_tested_last_12_months)
  );

  const failoverExistsAgg = aggregate(apps.map(a => calcFailoverSetup(a.failover_setup)));

  const failoverTestedResults = apps.map(a => calcDateLast12M(a.failover_tested_date_last_12_months));
  const failoverTestedAgg     = aggregate(failoverTestedResults);
  const { planned: fPlanned, unplanned: fUnplanned } = countPlannedUnplanned(
    apps.map(a => a.failover_tested_date_last_12_months)
  );

  const totalYes   = rpo.yes + rto.yes + trp.yes + backup.yes + restoreAgg.yes + failoverExistsAgg.yes + failoverTestedAgg.yes;
  const totalNonNA = rpo.nonNA + rto.nonNA + trp.nonNA + backup.nonNA + restoreAgg.nonNA + failoverExistsAgg.nonNA + failoverTestedAgg.nonNA;
  // Total % = average of only the non-NA metrics (exclude metrics where nonNA = 0)
  const metrics = [rpo, rto, trp, backup, restoreAgg, failoverExistsAgg, failoverTestedAgg];
  const nonNAMetrics = metrics.filter(m => m.nonNA > 0);
  const totalPct = nonNAMetrics.length > 0
    ? Math.round(nonNAMetrics.reduce((sum, m) => sum + m.pct, 0) / nonNAMetrics.length)
    : 0;

  return {
    portfolio,
    count: apps.length,
    rpo, rto, trp, backup,
    restore:        { ...restoreAgg,       planned: rPlanned, unplanned: rUnplanned },
    failoverExists: failoverExistsAgg,
    failoverTested: { ...failoverTestedAgg, planned: fPlanned, unplanned: fUnplanned },
    totalYes, totalNonNA, totalPct,
    platforms: {},
    appRows: apps,
  };
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const yesNoChip = (value: any) => {
  const v = toStr(value).toUpperCase();
  if (!v) return <Chip label="—" size="small" sx={{ fontSize: '0.7rem' }} />;
  // Handle boolean true/false coming from DB
  if (v === 'YES' || v === 'Y' || v === 'TRUE')
    return <Chip label="Yes" size="small" color="success" icon={<CheckCircle sx={{ fontSize: '0.85rem !important' }} />} sx={{ fontSize: '0.7rem' }} />;
  if (v === 'NO' || v === 'N' || v === 'FALSE')
    return <Chip label="No" size="small" color="error" icon={<Cancel sx={{ fontSize: '0.85rem !important' }} />} sx={{ fontSize: '0.7rem' }} />;
  if (v === 'NA' || v === 'N/A' || v === 'OOS')
    return <Chip label={toStr(value)} size="small" color="warning" icon={<Warning sx={{ fontSize: '0.85rem !important' }} />} sx={{ fontSize: '0.7rem' }} />;
  return <Chip label={toStr(value)} size="small" sx={{ fontSize: '0.7rem' }} />;
};

/** Render a pre-calculated CalcResult as 100% / 0% / NA for app-level rows */
const appPctCell = (result: CalcResult) => {
  if (result.status === 'NA')
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
        <HelpOutline sx={{ fontSize: 14, color: '#999' }} />
        <span style={{ color: '#999', fontSize: '12px' }}>NA</span>
      </Box>
    );
  const color = result.pct === 100 ? '#27ae60' : '#e74c3c';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.4 }}>
      <Circle sx={{ fontSize: 13, color }} />
      <span style={{ color, fontWeight: 700, fontSize: '13px' }}>{result.pct}%</span>
    </Box>
  );
};

/** Format any date-ish value to DD-MMM-YYYY, returning — if unparseable */
const formatDate = (value: any): string => {
  if (value === null || value === undefined) return '—';
  // Try native Date parse first (handles ISO strings, GMT strings, etc.)
  const d = new Date(value);
  if (!isNaN(d.getTime())) {
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  }
  // Try our custom parser
  const d2 = parseDateStr(value);
  if (d2) return d2.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  const s = toStr(value);
  return s || '—';
};

const MetricCell: React.FC<{ yes: number; nonNA: number; pct: number }> = ({ yes, nonNA, pct }) => {
  if (nonNA === 0)
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
        <HelpOutline sx={{ fontSize: 14, color: '#999' }} />
        <span style={{ color: '#999', fontSize: '12px' }}>NA</span>
      </Box>
    );
  const color = pct >= 80 ? '#27ae60' : pct >= 40 ? '#f39c12' : '#e74c3c';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.15 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
        <Circle sx={{ fontSize: 13, color }} />
        <span style={{ color, fontWeight: 700, fontSize: '13px' }}>{pct}%</span>
      </Box>
      <span style={{ color, fontSize: '12px' }}>{yes}/{nonNA}</span>
    </Box>
  );
};

const TH_STYLE = {
  fontWeight: 700,
  fontSize: '11px',
  whiteSpace: 'normal' as const,
  lineHeight: 1.4,
  bgcolor: '#1a1a2e',
  color: '#fff',
  textAlign: 'center' as const,
  borderRight: '1px solid rgba(255,255,255,0.08)',
  px: 2,
  py: 1.5,
};

// ─── Component ────────────────────────────────────────────────────────────────

const ResilienceMSAAssessment: React.FC = () => {
  const [data, setData]           = useState<MSARow[]>([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  // Filter options (all derived client-side from loaded data)
  const [portfolioOptions, setPortfolioOptions] = useState<string[]>([]);
  const [platformOptions, setPlatformOptions]   = useState<string[]>([]);
  const [applicationOptions, setApplicationOptions] = useState<string[]>([]);
  const [appTypeOptions, setAppTypeOptions]         = useState<string[]>([]);

  // Active filters — portfolio & platform are synced globally
  const {
    selectedPortfolio,
    selectedPlatform,
    setSelectedPortfolio,
    setSelectedPlatform,
  } = useGlobalFilters();
  const [selectedApplication, setSelectedApplication] = useState('All');
  const [selectedAppType, setSelectedAppType]     = useState<string[]>([]);
  const [appTypeOpen, setAppTypeOpen]             = useState(false);
  const [searchQuery, setSearchQuery]             = useState('');
  const [searchActive, setSearchActive]           = useState('');

  // Table state
  const [expandedPortfolios, setExpandedPortfolios] = useState<Record<string, boolean>>({});
  const [expandedPlatforms, setExpandedPlatforms]   = useState<Record<string, boolean>>({});

  // Track whether initial load has completed (so we don't reset user filters on subsequent fetches)
  const initialLoadDone = useRef(false);

  // ── Load all data — only resets filter selections on first load ──────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await authFetch(RESILIENCY_MSA.ALL);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}: ${resp.statusText}`);
      const result = await resp.json();
      const rows: MSARow[] = result.data || [];
      setData(rows);
      setLastRefresh(new Date());
      // Derive dropdown options from loaded data
      const portfolios = Array.from(new Set(rows.map((r: MSARow) => r.platform_portfolio).filter(Boolean))).sort() as string[];
      const platforms  = Array.from(new Set(rows.map((r: MSARow) => r.platform).filter(Boolean))).sort() as string[];
      const apps       = Array.from(new Set(rows.map((r: MSARow) => r.application).filter(Boolean))).sort() as string[];
      const appTypes   = Array.from(new Set(rows.map((r: MSARow) => (r.app_type || 'Non SaaS')))).sort() as string[];
      setPortfolioOptions(portfolios);
      setPlatformOptions(platforms);
      setApplicationOptions(apps);
      setAppTypeOptions(appTypes);
      // Only set default app type selection on first load — preserve user selections after that
      if (!initialLoadDone.current) {
        const defaultTypes = appTypes.filter((t: string) => t !== 'NGA' && t !== 'SaaS');
        setSelectedAppType(defaultTypes);
        initialLoadDone.current = true;
      }
    } catch (err: any) {
      setError(err.message || 'Unknown error');
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Initial load on mount ────────────────────────────────────────────────────
  useEffect(() => { loadData(); }, [loadData]);

  // All filtering (portfolio, platform, application, app type) is done client-side
  // No re-fetches needed after initial load — use the Refresh button for manual re-fetch

  // ── Cascade-derived platform / application options ─────────────────────────
  const filteredPlatformOptions: string[] = selectedPortfolio !== 'all'
    ? Array.from(new Set(data.filter((r: MSARow) => r.platform_portfolio === selectedPortfolio).map((r: MSARow) => r.platform).filter(Boolean))).sort() as string[]
    : platformOptions;

  const filteredApplicationOptions: string[] = (selectedPortfolio !== 'all' || selectedPlatform !== 'all')
    ? Array.from(new Set(
        data
          .filter((r: MSARow) =>
            (selectedPortfolio === 'all' || r.platform_portfolio === selectedPortfolio) &&
            (selectedPlatform  === 'all' || r.platform === selectedPlatform)
          )
          .map((r: MSARow) => r.application)
          .filter(Boolean)
      )).sort() as string[]
    : applicationOptions;

  // ── Client-side filter ───────────────────────────────────────────────────────
  const filteredData = data.filter((row: MSARow) => {
    if (selectedPortfolio !== 'all' && row.platform_portfolio !== selectedPortfolio) return false;
    if (selectedPlatform  !== 'all' && row.platform           !== selectedPlatform)  return false;
    if (selectedApplication !== 'All' && row.application      !== selectedApplication) return false;
    if (selectedAppType.length > 0 && !selectedAppType.some((t: string) => t === (row.app_type || 'Non SaaS'))) return false;
    if (searchActive) {
      const q = searchActive.toLowerCase();
      return (
        (row.application || '').toLowerCase().indexOf(q) !== -1 ||
        (row.app_id      || '').toLowerCase().indexOf(q) !== -1
      );
    }
    return true;
  });

  // ── Build metrics overview ───────────────────────────────────────────────────
  const portfolioMetrics: PortfolioMetrics[] = [];
  const portfolioGroups: Record<string, MSARow[]> = {};
  filteredData.forEach(row => {
    const p = row.platform_portfolio || 'Unknown';
    if (!portfolioGroups[p]) portfolioGroups[p] = [];
    portfolioGroups[p].push(row);
  });

  Object.keys(portfolioGroups).sort((a: string, b: string) => a.localeCompare(b)).forEach((portfolio: string) => {
    const apps: MSARow[] = portfolioGroups[portfolio];
    const pm = buildMetrics(apps, portfolio);
    // Build platform sub-metrics
    const platformGroups: Record<string, MSARow[]> = {};
    apps.forEach((row: MSARow) => {
      const pl = row.platform || 'Unknown';
      if (!platformGroups[pl]) platformGroups[pl] = [];
      platformGroups[pl].push(row);
    });
    Object.keys(platformGroups).sort((a: string, b: string) => a.localeCompare(b)).forEach((platform: string) => {
      const pApps: MSARow[] = platformGroups[platform];
      pm.platforms[platform] = buildMetrics(pApps, platform);
    });
    portfolioMetrics.push(pm);
  });

  // ── Grand total ──────────────────────────────────────────────────────────────
  const grandTotal = buildMetrics(filteredData, 'Total');

  // ── CSV Export ───────────────────────────────────────────────────────────────
  const handleExport = () => {
    const headers = [
      'Platform Portfolio', 'Count of Apps', 'App Type',
      'RPO Business Expectation', 'RTO Business Expectation', 'TRP Exists',
      'Backup Available',
      'Restore from backup Tested (in the last 12 months)', 'Planned Restore Tests Count', 'Unplanned Restore Test Count',
      'Failover Setup', 'Failover Tested (in the last 12 months)', 'Planned Failover Tests Count', 'Unplanned Failover Test Count',
      'Total %',
    ];

    const fmtPct = (yes: number, nonNA: number, pct: number) =>
      nonNA === 0 ? 'NA' : `${pct}% (${yes}/${nonNA})`;

    const portfolioRows = portfolioMetrics.map(pm => [
      pm.portfolio,
      pm.count,
      '-',
      fmtPct(pm.rpo.yes, pm.rpo.nonNA, pm.rpo.pct),
      fmtPct(pm.rto.yes, pm.rto.nonNA, pm.rto.pct),
      fmtPct(pm.trp.yes, pm.trp.nonNA, pm.trp.pct),
      fmtPct(pm.backup.yes, pm.backup.nonNA, pm.backup.pct),
      fmtPct(pm.restore.yes, pm.restore.nonNA, pm.restore.pct),
      pm.restore.planned,
      pm.restore.unplanned,
      fmtPct(pm.failoverExists.yes, pm.failoverExists.nonNA, pm.failoverExists.pct),
      fmtPct(pm.failoverTested.yes, pm.failoverTested.nonNA, pm.failoverTested.pct),
      pm.failoverTested.planned,
      pm.failoverTested.unplanned,
      `${pm.totalPct}%`,
    ]);

    // Grand total row (matches Total row in UI)
    const gt = grandTotal;
    const totalRow = [
      'Total',
      gt.count,
      '-',
      fmtPct(gt.rpo.yes, gt.rpo.nonNA, gt.rpo.pct),
      fmtPct(gt.rto.yes, gt.rto.nonNA, gt.rto.pct),
      fmtPct(gt.trp.yes, gt.trp.nonNA, gt.trp.pct),
      fmtPct(gt.backup.yes, gt.backup.nonNA, gt.backup.pct),
      fmtPct(gt.restore.yes, gt.restore.nonNA, gt.restore.pct),
      gt.restore.planned,
      gt.restore.unplanned,
      fmtPct(gt.failoverExists.yes, gt.failoverExists.nonNA, gt.failoverExists.pct),
      fmtPct(gt.failoverTested.yes, gt.failoverTested.nonNA, gt.failoverTested.pct),
      gt.failoverTested.planned,
      gt.failoverTested.unplanned,
      `${gt.totalPct}%`,
    ];

    const csv = [
      headers.map(h => `"${h}"`).join(','),
      ...portfolioRows.map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')),
      totalRow.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = `resilience_msa_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ backgroundColor: '#ffffff', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2, color: '#0067b3' }} />
          <Typography variant="h6" color="textSecondary">
            Loading Resilience Assessment Data...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3 }}>
      <Alert
        severity="warning"
        icon={<Warning sx={{ fontSize: 30 }} />}
        sx={{
          mt: 2,
          mb: 2,
          border: '1px solid #f59e0b',
          borderLeft: '6px solid #d97706',
          borderRadius: 1,
          backgroundColor: '#fff8e1',
          boxShadow: '0 2px 8px rgba(180, 83, 9, 0.12)',
          '& .MuiAlert-icon': { color: '#b45309', alignItems: 'flex-start', mt: 0.25 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, width: '100%' }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 700, color: '#7c2d12', mb: 0.5 }}>
              Dashboard Migration Notice
            </Typography>
            <Typography variant="body1" sx={{ color: '#7c2d12', lineHeight: 1.6 }}>
              As part of our transition to MTS, this dashboard will be decommissioned from Operational Excellence on <strong>15th October</strong>. This link will redirect automatically to MTS after that date.
            </Typography>
            <Typography variant="body2" sx={{ color: '#7c2d12', mt: 0.5 }}>
              Need help? Visit the <strong>MTS Support Channel</strong>.
            </Typography>
          </Box>
          <Button component="a" href="https://mts.maersk.io/bowler/dr" target="_blank" rel="noopener noreferrer" variant="contained" sx={{ flexShrink: 0, bgcolor: '#b45309', '&:hover': { bgcolor: '#92400e' } }}>
            Open MTS
          </Button>
        </Box>
      </Alert>
      {/* Filters */}
      <Paper sx={{ mx: 0, mt: 2, mb: 2, p: 2, backgroundColor: '#f8f9fa', border: '1px solid #e1e5e9', borderRadius: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterList sx={{ color: '#2c3e50', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2c3e50' }}>Filters</Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '11px', mt: 0.5 }}>
              Data Source: <a
                href="https://msa.maersk.io/"
                target="_blank" rel="noopener noreferrer"
                style={{ color: '#0067b3', textDecoration: 'underline' }}
              >MSA</a>
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined" startIcon={<GetApp />} onClick={handleExport}
              disabled={loading || filteredData.length === 0} size="small"
              sx={{ borderColor: '#00A8CC', color: '#00A8CC', '&:hover': { borderColor: '#0067b3', bgcolor: 'rgba(0,168,204,0.04)' }, textTransform: 'none' }}
            >
              Export CSV
            </Button>
          </Box>
        </Box>
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '13px' }}>Platform Portfolio</InputLabel>
              <Select
                value={selectedPortfolio}
                label="Platform Portfolio"
                onChange={e => { setSelectedPortfolio(e.target.value); setSelectedPlatform('all'); setSelectedApplication('All'); }}
                sx={{ backgroundColor: '#ffffff', fontSize: '13px', '& .MuiSelect-select': { py: 1 } }}
              >
                <MenuItem value="all">All</MenuItem>
                {portfolioOptions.map(p => <MenuItem key={p} value={p} sx={{ fontSize: '13px' }}>{p}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '13px' }}>Platform</InputLabel>
              <Select
                value={selectedPlatform}
                label="Platform"
                onChange={e => {
                  const platform = e.target.value;
                  setSelectedPlatform(platform);
                  setSelectedApplication('All');
                  // Auto-set portfolio to match the selected platform
                  if (platform !== 'all') {
                    const match = data.find((r: MSARow) => r.platform === platform);
                    if (match?.platform_portfolio) setSelectedPortfolio(match.platform_portfolio);
                  }
                }}
                sx={{ backgroundColor: '#ffffff', fontSize: '13px', '& .MuiSelect-select': { py: 1 } }}
              >
                <MenuItem value="all">All</MenuItem>
                {filteredPlatformOptions.map(p => <MenuItem key={p} value={p} sx={{ fontSize: '13px' }}>{p}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '13px' }}>Application</InputLabel>
              <Select
                value={selectedApplication}
                label="Application"
                onChange={e => {
                  const appName = e.target.value;
                  setSelectedApplication(appName);
                  if (appName !== 'All') {
                    const found = data.find((r: MSARow) => r.application === appName);
                    if (found) {
                      setSelectedPortfolio(found.platform_portfolio || 'all');
                      setSelectedPlatform(found.platform || 'all');
                    }
                  }
                }}
                sx={{ backgroundColor: '#ffffff', fontSize: '13px', '& .MuiSelect-select': { py: 1 } }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
              >
                <MenuItem value="All" sx={{ fontSize: '13px', whiteSpace: 'normal' }}>All</MenuItem>
                {filteredApplicationOptions.map(a => <MenuItem key={a} value={a} sx={{ fontSize: '13px', whiteSpace: 'normal' }}>{a}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ fontSize: '13px' }}>App Type</InputLabel>
              <Select
                multiple
                open={appTypeOpen}
                onOpen={() => setAppTypeOpen(true)}
                onClose={() => setAppTypeOpen(false)}
                value={selectedAppType}
                label="App Type"
                onChange={e => {
                  const val = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value as string[];
                  setSelectedAppType(val);
                }}
                renderValue={(selected: string[]) => selected.length === 0 ? 'All' : selected.join(', ')}
                sx={{ backgroundColor: '#ffffff', fontSize: '13px', '& .MuiSelect-select': { py: 1 } }}
                MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
              >
                {appTypeOptions.map(t => (
                  <MenuItem key={t} value={t} sx={{ fontSize: '13px' }}>
                    <Checkbox checked={selectedAppType.includes(t)} size="small" />
                    <ListItemText primary={t} primaryTypographyProps={{ fontSize: '13px' }} />
                  </MenuItem>
                ))}
                <ListSubheader
                  disableSticky={false}
                  sx={{ position: 'sticky', bottom: 0, bgcolor: '#fff', borderTop: '1px solid #e0e0e0', zIndex: 1, py: 0.5 }}
                >
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'space-between', p: 0.5 }}>
                    <Button
                      size="small" variant="contained"
                      onMouseDown={e => { e.preventDefault(); setSelectedAppType([...appTypeOptions]); }}
                      sx={{ textTransform: 'none', fontSize: '12px', backgroundColor: '#0067b3' }}
                    >Select All</Button>
                    <Button
                      size="small" variant="text" color="secondary"
                      onMouseDown={e => { e.preventDefault(); setSelectedAppType([]); }}
                      sx={{ textTransform: 'none', fontSize: '12px' }}
                    >Clear All</Button>
                  </Box>
                </ListSubheader>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                size="small" fullWidth placeholder="Search by App ID or Name" value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') setSearchActive(searchQuery); }}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                sx={{ backgroundColor: '#ffffff', fontSize: '13px', '& input::placeholder': { fontSize: '11px' }, '& input': { fontSize: '12px' } }}
              />
              <Button variant="contained" size="small"
                onClick={() => setSearchActive(searchQuery)}
                sx={{ backgroundColor: '#0067b3', textTransform: 'none', whiteSpace: 'nowrap', px: 2 }}
              >Search</Button>
            </Box>
          </Grid>
        </Grid>
        {(selectedPortfolio !== 'all' || selectedPlatform !== 'all' || selectedApplication !== 'All' || selectedAppType.length > 0 || searchActive) && (
          <Box sx={{ mt: 1.5 }}>
            <Button size="small" variant="text" color="secondary"
              onClick={() => { setSelectedPortfolio('all'); setSelectedPlatform('all'); setSelectedApplication('All'); setSelectedAppType([]); setSearchQuery(''); setSearchActive(''); }}>
              Clear filters
            </Button>
          </Box>
        )}
      </Paper>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      ) : (
        <>
          {/* ── Metrics Overview Table ──────────────────────────────────────── */}
          <Paper elevation={3} sx={{ mb: 4 }}>
            <Typography variant="h6" fontWeight="bold" sx={{ p: '10px 16px' }}>
              Resilience Metrics Overview
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...TH_STYLE, textAlign: 'left', minWidth: 190 }}>Platform Portfolio</TableCell>
                    <TableCell sx={{ ...TH_STYLE, minWidth: 75 }}>App ID</TableCell>
                    <TableCell sx={{ ...TH_STYLE, minWidth: 75 }}>Count of Apps</TableCell>
                    <TableCell sx={{ ...TH_STYLE, minWidth: 95 }}>App Type</TableCell>
                    <TableCell sx={{ ...TH_STYLE, minWidth: 105 }}>RPO Business Expectation</TableCell>
                    <TableCell sx={{ ...TH_STYLE, minWidth: 105 }}>RTO Business Expectation</TableCell>
                    <TableCell sx={{ ...TH_STYLE, minWidth: 95 }}>TRP Exists</TableCell>
                    <TableCell sx={{ ...TH_STYLE, minWidth: 95 }}>Backup Available</TableCell>
                    <TableCell sx={{ ...TH_STYLE, minWidth: 150 }}>Restore from backup Tested (in the last 12 months)</TableCell>
                    <TableCell sx={{ ...TH_STYLE, minWidth: 105 }}>Planned Restore Tests Count</TableCell>
                    <TableCell sx={{ ...TH_STYLE, minWidth: 105 }}>Unplanned Restore Test Count</TableCell>
                    <TableCell sx={{ ...TH_STYLE, minWidth: 95 }}>Failover Setup</TableCell>
                    <TableCell sx={{ ...TH_STYLE, minWidth: 150 }}>Failover Tested (in the last 12 months)</TableCell>
                    <TableCell sx={{ ...TH_STYLE, minWidth: 105 }}>Planned Failover Tests Count</TableCell>
                    <TableCell sx={{ ...TH_STYLE, minWidth: 105 }}>Unplanned Failover Test Count</TableCell>
                    <TableCell sx={{ ...TH_STYLE, minWidth: 80 }}>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {portfolioMetrics.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={16} align="center" sx={{ py: 4, color: '#888' }}>No data</TableCell>
                    </TableRow>
                  ) : (
                    <>
                      {portfolioMetrics.map(pm => {
                        const expanded = !!expandedPortfolios[pm.portfolio];
                        return (
                          <React.Fragment key={pm.portfolio}>
                            {/* Portfolio row */}
                            <TableRow
                              hover
                              onClick={() => setExpandedPortfolios(prev => ({ ...prev, [pm.portfolio]: !prev[pm.portfolio] }))}
                              sx={{ cursor: 'pointer', bgcolor: expanded ? '#f0f7ff' : 'transparent', '&:hover': { bgcolor: '#f5f5f5' } }}
                            >
                              <TableCell sx={{ fontSize: '12px', fontWeight: 600 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {expanded
                                    ? <ExpandLess fontSize="small" sx={{ color: '#1976d2' }} />
                                    : <ExpandMore fontSize="small" sx={{ color: '#1976d2' }} />}
                                  <span style={{ color: '#1976d2' }}>{pm.portfolio}</span>
                                </Box>
                              </TableCell>
                              <TableCell align="center" sx={{ fontSize: '12px', color: '#888' }}>-</TableCell>
                              <TableCell align="center" sx={{ fontSize: '12px', fontWeight: 600 }}>{pm.count}</TableCell>
                              <TableCell align="center" sx={{ fontSize: '12px', color: '#888' }}>-</TableCell>
                              <TableCell align="center"><MetricCell {...pm.rpo} /></TableCell>
                              <TableCell align="center"><MetricCell {...pm.rto} /></TableCell>
                              <TableCell align="center"><MetricCell {...pm.trp} /></TableCell>
                              <TableCell align="center"><MetricCell {...pm.backup} /></TableCell>
                              <TableCell align="center"><MetricCell yes={pm.restore.yes} nonNA={pm.restore.nonNA} pct={pm.restore.pct} /></TableCell>
                              <TableCell align="center" sx={{ fontSize: '12px' }}>{pm.restore.planned}</TableCell>
                              <TableCell align="center" sx={{ fontSize: '12px' }}>{pm.restore.unplanned}</TableCell>
                              <TableCell align="center"><MetricCell {...pm.failoverExists} /></TableCell>
                              <TableCell align="center"><MetricCell yes={pm.failoverTested.yes} nonNA={pm.failoverTested.nonNA} pct={pm.failoverTested.pct} /></TableCell>
                              <TableCell align="center" sx={{ fontSize: '12px' }}>{pm.failoverTested.planned}</TableCell>
                              <TableCell align="center" sx={{ fontSize: '12px' }}>{pm.failoverTested.unplanned}</TableCell>
                              <TableCell align="center"><MetricCell yes={pm.totalYes} nonNA={pm.totalNonNA} pct={pm.totalPct} /></TableCell>
                            </TableRow>

                            {/* Platform sub-rows */}
                            {expanded && Object.keys(pm.platforms).sort().map((platform: string) => {
                              const plm: PortfolioMetrics = pm.platforms[platform];
                              const platKey = `${pm.portfolio}||${platform}`;
                              const platExpanded = !!expandedPlatforms[platKey];
                              return (
                              <React.Fragment key={platKey}>
                                {/* Platform row — clickable to expand to app rows */}
                                <TableRow
                                  hover
                                  onClick={() => setExpandedPlatforms(prev => ({ ...prev, [platKey]: !prev[platKey] }))}
                                  sx={{ cursor: 'pointer', bgcolor: platExpanded ? '#e8f4fd' : '#f0f7ff', '&:hover': { bgcolor: '#ddeeff' } }}
                                >
                                  <TableCell sx={{ fontSize: '11px', pl: 4, color: '#666' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {platExpanded
                                        ? <ExpandLess fontSize="small" sx={{ fontSize: 14, color: '#666' }} />
                                        : <ExpandMore fontSize="small" sx={{ fontSize: 14, color: '#666' }} />}
                                      <CloudOutlined fontSize="small" sx={{ color: '#666' }} />
                                      <span style={{ color: '#666' }}>{platform}</span>
                                    </Box>
                                  </TableCell>
                                  <TableCell align="center" sx={{ fontSize: '11px', color: '#888' }}>-</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '11px' }}>{plm.count}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '11px', color: '#888' }}>-</TableCell>
                                  <TableCell align="center"><MetricCell {...plm.rpo} /></TableCell>
                                  <TableCell align="center"><MetricCell {...plm.rto} /></TableCell>
                                  <TableCell align="center"><MetricCell {...plm.trp} /></TableCell>
                                  <TableCell align="center"><MetricCell {...plm.backup} /></TableCell>
                                  <TableCell align="center"><MetricCell yes={plm.restore.yes} nonNA={plm.restore.nonNA} pct={plm.restore.pct} /></TableCell>
                                  <TableCell align="center" sx={{ fontSize: '11px' }}>{plm.restore.planned}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '11px' }}>{plm.restore.unplanned}</TableCell>
                                  <TableCell align="center"><MetricCell {...plm.failoverExists} /></TableCell>
                                  <TableCell align="center"><MetricCell yes={plm.failoverTested.yes} nonNA={plm.failoverTested.nonNA} pct={plm.failoverTested.pct} /></TableCell>
                                  <TableCell align="center" sx={{ fontSize: '11px' }}>{plm.failoverTested.planned}</TableCell>
                                  <TableCell align="center" sx={{ fontSize: '11px' }}>{plm.failoverTested.unplanned}</TableCell>
                                  <TableCell align="center"><MetricCell yes={plm.totalYes} nonNA={plm.totalNonNA} pct={plm.totalPct} /></TableCell>
                                </TableRow>

                                {/* Individual application rows — show exact raw API values */}
                                {platExpanded && plm.appRows.map((app: MSARow) => (
                                  <TableRow key={`app-${app.id}`} sx={{ bgcolor: '#f5f5f5', '&:hover': { bgcolor: '#eef6ff' } }}>
                                    <TableCell sx={{ fontSize: '10px', pl: 8 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 2 }}>
                                        <Apps fontSize="small" sx={{ color: '#999', fontSize: '16px' }} />
                                        <span style={{ color: '#999' }}>{app.application || 'NA'}</span>
                                      </Box>
                                    </TableCell>
                                    <TableCell align="center" sx={{ fontSize: '11px', color: '#333' }}>{toStr(app.app_id) || 'NA'}</TableCell>
                                    <TableCell align="center" sx={{ fontSize: '11px', color: '#888' }}>1</TableCell>
                                    <TableCell align="center" sx={{ fontSize: '11px' }}>{toStr(app.app_type) || '0'}</TableCell>
                                    {(() => {
                                      const rpo = calcRpoRto(app.rpo_business_expectation);
                                      const rto = calcRpoRto(app.rto_business_expectation);
                                      const trp = calcTrpAvailable(app.trp_available);
                                      const backup = calcSimpleYesNo(app.backup_available);
                                      const restore = calcDateLast12M(app.restore_from_backup_tested_last_12_months);
                                      const failoverExists = calcFailoverSetup(app.failover_setup);
                                      const failoverTested = calcDateLast12M(app.failover_tested_date_last_12_months);
                                      const { planned: rPlanned, unplanned: rUnplanned } = countPlannedUnplanned([app.restore_from_backup_tested_last_12_months]);
                                      const { planned: fPlanned, unplanned: fUnplanned } = countPlannedUnplanned([app.failover_tested_date_last_12_months]);
                                      // Total % = average of only the non-NA metrics (exclude metrics where status = 'NA')
                                      const metrics = [rpo, rto, trp, backup, restore, failoverExists, failoverTested];
                                      const nonNAMetrics = metrics.filter(m => m.status !== 'NA');
                                      const totalPct = nonNAMetrics.length > 0
                                        ? Math.round(nonNAMetrics.reduce((sum, m) => sum + m.pct, 0) / nonNAMetrics.length)
                                        : 0;
                                      const totalColor = totalPct >= 80 ? '#27ae60' : totalPct >= 40 ? '#f39c12' : '#e74c3c';
                                      return (
                                        <>
                                          <TableCell align="center">{appPctCell(rpo)}</TableCell>
                                          <TableCell align="center">{appPctCell(rto)}</TableCell>
                                          <TableCell align="center">{appPctCell(trp)}</TableCell>
                                          <TableCell align="center">{appPctCell(backup)}</TableCell>
                                          <TableCell align="center">{appPctCell(restore)}</TableCell>
                                          <TableCell align="center" sx={{ fontSize: '11px', fontWeight: 600 }}>{rPlanned}</TableCell>
                                          <TableCell align="center" sx={{ fontSize: '11px', fontWeight: 600 }}>{rUnplanned}</TableCell>
                                          <TableCell align="center">{appPctCell(failoverExists)}</TableCell>
                                          <TableCell align="center">{appPctCell(failoverTested)}</TableCell>
                                          <TableCell align="center" sx={{ fontSize: '11px', fontWeight: 600 }}>{fPlanned}</TableCell>
                                          <TableCell align="center" sx={{ fontSize: '11px', fontWeight: 600 }}>{fUnplanned}</TableCell>
                                          <TableCell align="center">
                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.4 }}>
                                              <Circle sx={{ fontSize: 13, color: totalColor }} />
                                              <span style={{ color: totalColor, fontWeight: 700, fontSize: '13px' }}>{totalPct}%</span>
                                            </Box>
                                          </TableCell>
                                        </>
                                      );
                                    })()}
                                  </TableRow>
                                ))}
                              </React.Fragment>
                            ); })}
                          </React.Fragment>
                        );
                      })}

                      {/* Grand Total row */}
                      <TableRow sx={{ bgcolor: '#f5f5f5', borderTop: '2px solid #ddd' }}>
                        <TableCell sx={{ fontSize: '12px', fontWeight: 700 }}>Total</TableCell>
                        <TableCell align="center" sx={{ fontSize: '12px', color: '#888' }}>-</TableCell>
                        <TableCell align="center" sx={{ fontSize: '12px', fontWeight: 700 }}>{grandTotal.count}</TableCell>
                        <TableCell align="center" sx={{ fontSize: '12px', color: '#888' }}>-</TableCell>
                        <TableCell align="center"><MetricCell {...grandTotal.rpo} /></TableCell>
                        <TableCell align="center"><MetricCell {...grandTotal.rto} /></TableCell>
                        <TableCell align="center"><MetricCell {...grandTotal.trp} /></TableCell>
                        <TableCell align="center"><MetricCell {...grandTotal.backup} /></TableCell>
                        <TableCell align="center"><MetricCell yes={grandTotal.restore.yes} nonNA={grandTotal.restore.nonNA} pct={grandTotal.restore.pct} /></TableCell>
                        <TableCell align="center" sx={{ fontSize: '12px', fontWeight: 600 }}>{grandTotal.restore.planned}</TableCell>
                        <TableCell align="center" sx={{ fontSize: '12px', fontWeight: 600 }}>{grandTotal.restore.unplanned}</TableCell>
                        <TableCell align="center"><MetricCell {...grandTotal.failoverExists} /></TableCell>
                        <TableCell align="center"><MetricCell yes={grandTotal.failoverTested.yes} nonNA={grandTotal.failoverTested.nonNA} pct={grandTotal.failoverTested.pct} /></TableCell>
                        <TableCell align="center" sx={{ fontSize: '12px', fontWeight: 600 }}>{grandTotal.failoverTested.planned}</TableCell>
                        <TableCell align="center" sx={{ fontSize: '12px', fontWeight: 600 }}>{grandTotal.failoverTested.unplanned}</TableCell>
                        <TableCell align="center"><MetricCell yes={grandTotal.totalYes} nonNA={grandTotal.totalNonNA} pct={grandTotal.totalPct} /></TableCell>
                      </TableRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}
    </Box>
  );
};

export default ResilienceMSAAssessment;